import React from 'react';
import { LineTd, LineInput } from './SharedStyles';

/**
 * SizeLineRow — dimensional-inventory ("Has Size" / SQft) input strip.
 *
 * Renders as its OWN table row placed directly under a line item's main row.
 * The first `leftColSpan` columns (# and Item) are left blank; a single wide
 * cell then spans from the Description column to the END of the row (a large
 * colSpan is clamped by the browser to however many columns remain, so the same
 * component fits every document regardless of its right-hand columns). This
 * gives the L × H × T / pieces / sqft fields a full-width strip so nothing wraps
 * or gets clipped — the old layout crammed them inside the ~260px Description
 * cell, where values overlapped and sometimes didn't show at all.
 *
 * Purely presentational: it uses the same onChange(index, field, value) handler
 * and the pre-computed line.sqftPerPiece / line.totalSqft as before, so no
 * calculation or stored-data behaviour changes.
 *
 * @param {object}   line         the line item (needs length/height/thickness/pieces/sizeUnit/sqftPerPiece/totalSqft)
 * @param {number}   index        line index, passed straight back to onChange
 * @param {function} onChange     (index, field, value) => void
 * @param {boolean}  disabled     disables every input (e.g. sourced/linked lines)
 * @param {number}   [leftColSpan=2] blank columns before Description (# and Item)
 */
const cellBg = { background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', padding: '2px 8px 6px' };
const numStyle = { width: 78, textAlign: 'right' };

const SizeLineRow = ({ line, index, onChange, disabled = false, leftColSpan = 2 }) => (
  <tr>
    <LineTd colSpan={leftColSpan} style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }} />
    <LineTd colSpan={99} style={cellBg}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 12 }}>
        <span style={{ color: '#64748B', fontWeight: 600 }}>Size:</span>
        <LineInput type="number" min="0" step="0.01" placeholder="L" value={line.length || 0}
          onChange={e => onChange(index, 'length', Number(e.target.value))}
          disabled={disabled} style={numStyle} />
        <span>×</span>
        <LineInput type="number" min="0" step="0.01" placeholder="H" value={line.height || 0}
          onChange={e => onChange(index, 'height', Number(e.target.value))}
          disabled={disabled} style={numStyle} />
        <span>× T</span>
        <LineInput type="number" min="0" step="0.01" placeholder="0" value={line.thickness || 0}
          onChange={e => onChange(index, 'thickness', Number(e.target.value))}
          disabled={disabled} style={{ width: 62, textAlign: 'right' }} />
        <span style={{ color: '#94A3B8' }}>{line.sizeUnit || 'inches'}</span>
        <span style={{ marginLeft: 6, color: '#64748B', fontWeight: 600 }}>· Pieces:</span>
        <LineInput type="number" min="0" step="1" placeholder="0" value={line.pieces || 0}
          onChange={e => onChange(index, 'pieces', Number(e.target.value))}
          disabled={disabled} style={numStyle} />
        <span style={{ marginLeft: 8, color: '#475569' }}>
          = <strong>{(line.sqftPerPiece || 0).toFixed(2)}</strong> sqft/pc · Total <strong>{(line.totalSqft || 0).toFixed(2)}</strong> SQft
        </span>
      </div>
    </LineTd>
  </tr>
);

export default SizeLineRow;
